"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const client_1 = require("@apollo/client");
const context_1 = require("@apollo/client/link/context");
const retry_1 = require("@apollo/client/link/retry");
const enums_1 = __importDefault(require("@aurora/shared-types/authentication/enums"));
const enums_2 = require("@aurora/shared-types/community/enums");
const OriginHelper_1 = __importDefault(require("@aurora/shared-utils/authentication/OriginHelper"));
const closeResponse_1 = __importDefault(require("@aurora/shared-utils/fetch/closeResponse"));
const AuthHeaderHelper_1 = __importDefault(require("@aurora/shared-utils/helpers/authentication/AuthHeaderHelper"));
const ConfigHelper_1 = __importDefault(require("@aurora/shared-utils/helpers/ConfigHelper"));
const log_1 = require("@aurora/shared-utils/log");
const exenv_1 = require("exenv");
const FetchError_1 = require("./FetchError");
const SwitchUserHelper_1 = require("./helpers/SwitchUserHelper");
const log = (0, log_1.getLog)(module);
function isQueryOperation(operation) {
    return (0, client_1.parser)(operation.query).type === client_1.DocumentType.Query;
}
let tokenTimeoutSet = false;
function authLink(initialToken, tokenResultCallback, logoutHandler, tenant) {
    // initialize the auth token from the Next page context here
    if (initialToken) {
        log.debug('Creating new authLink with initial token: %O', initialToken);
    }
    let currentTokenResult = initialToken;
    function invalidateLocalStorageItems(key) {
        if (exenv_1.canUseDOM && (localStorage === null || localStorage === void 0 ? void 0 : localStorage.getItem(key)) != null) {
            localStorage.removeItem(key);
            localStorage.removeItem(enums_2.LocalStorageKeys.ANALYTICS_DASHBOARD_CONFIGS);
            localStorage.removeItem(enums_2.LocalStorageKeys.ANALYTICS_REPORTS_DATE_RANGE);
            localStorage.removeItem(enums_2.LocalStorageKeys.ANALYTICS_DATETIME_VALUE);
            localStorage.removeItem(enums_2.LocalStorageKeys.ANALYTICS_WIDGET_CONFIG);
            localStorage.removeItem(enums_2.LocalStorageKeys.ANALYTICS_REPORTS_CONFIGS);
            return true;
        }
        return false;
    }
    function checkAuthentication(operation) {
        return __awaiter(this, void 0, void 0, function* () {
            log.debug('Checking authentication...');
            const updatedTokenResult = yield tokenResultCallback();
            // if auth provider has changed, fire logout event
            if (exenv_1.canUseDOM &&
                (!operation || isQueryOperation(operation)) &&
                currentTokenResult &&
                currentTokenResult.providerName != (updatedTokenResult === null || updatedTokenResult === void 0 ? void 0 : updatedTokenResult.providerName)) {
                log.info('Auth has changed. Logging out.');
                currentTokenResult = updatedTokenResult;
                window.dispatchEvent(new CustomEvent('kh:logout'));
            }
            return updatedTokenResult;
        });
    }
    if (exenv_1.canUseDOM) {
        invalidateLocalStorageItems(enums_2.LocalStorageKeys.AUTH_INVALIDATING_KEY);
        function startTokenRefreshTask() {
            let timeout;
            if (currentTokenResult && !tokenTimeoutSet) {
                tokenTimeoutSet = true;
                const { expires = 0 } = currentTokenResult;
                if (expires > 0) {
                    log.info('Auth refresh interval is every %d seconds', expires);
                    timeout = setInterval(() => __awaiter(this, void 0, void 0, function* () {
                        tokenTimeoutSet = false;
                        // update the current token result and check for auth provider changes
                        const updatedTokenResult = yield checkAuthentication();
                        if (updatedTokenResult) {
                            log.debug('Auth refreshed');
                            currentTokenResult = updatedTokenResult;
                        }
                        else {
                            log.debug('Auth not refreshed as token result is null');
                        }
                    }), expires * 1000);
                }
            }
            return timeout;
        }
        const timeout = startTokenRefreshTask();
        const listener = () => {
            if (timeout) {
                clearTimeout(timeout);
            }
        };
        window.removeEventListener('beforeunload', listener);
        window.addEventListener('beforeunload', listener);
    }
    function buildHeaders(contextHeaders, tokenResult) {
        var _a;
        const userType = (0, SwitchUserHelper_1.getSwitchUserTypeFromCookie)((_a = tenant === null || tenant === void 0 ? void 0 : tenant.publicConfig) === null || _a === void 0 ? void 0 : _a.sdkEnabled, contextHeaders === null || contextHeaders === void 0 ? void 0 : contextHeaders.cookie);
        const authHeaders = Object.assign(Object.assign(Object.assign({}, contextHeaders), OriginHelper_1.default.getOriginHeaders()), (0, AuthHeaderHelper_1.default)(tokenResult, userType));
        log.trace('Auth headers are %O', authHeaders);
        return {
            headers: authHeaders
        };
    }
    const retryAttempts = ConfigHelper_1.default.toInteger(process.env.NEXT_PUBLIC_AUTH_RETRY_ATTEMPTS, 10);
    const handleRetry = (retryCount, operation, error) => __awaiter(this, void 0, void 0, function* () {
        log.trace('Retrying operation: %s', operation === null || operation === void 0 ? void 0 : operation.operationName);
        if (retryCount <= retryAttempts) {
            if (logoutHandler && (0, FetchError_1.isUnauthorizedError)(error)) {
                log.trace('Verifying auth.');
                // verify auth token -- this will refresh the token as necessary
                yield logoutHandler(enums_1.default.VERIFY_TOKEN);
                const updatedTokenResult = yield checkAuthentication(operation);
                if (updatedTokenResult) {
                    log.debug('Retry attempt %d for operation %s', retryCount, operation === null || operation === void 0 ? void 0 : operation.operationName);
                    currentTokenResult = updatedTokenResult;
                    operation.setContext(({ headers = {} }) => buildHeaders(headers, currentTokenResult));
                    return true;
                }
            }
            else if ((0, FetchError_1.isTooManyRequestsError)(error) || (0, FetchError_1.isTooBusyError)(error)) {
                log.info('Site is too busy. Retrying the operation.');
                return true;
            }
        }
        else {
            log.warn('Retry attempts (%d) exhausted', retryAttempts);
        }
        if ('response' in error) {
            yield (0, closeResponse_1.default)(error.response);
        }
        return false;
    });
    const authHeaderLink = (0, context_1.setContext)((_1, _a) => __awaiter(this, [_1, _a], void 0, function* (_, { headers }) {
        // if invalidation key as set in local storage, invalidate the current token
        if (invalidateLocalStorageItems(enums_2.LocalStorageKeys.AUTH_INVALIDATION_KEY)) {
            log.trace('Auth invalidation key set.  Resetting current auth token.');
            currentTokenResult = null;
        }
        // get a token if none was provided initially
        if (!currentTokenResult) {
            log.debug('Fetching new user auth');
            currentTokenResult = yield tokenResultCallback();
        }
        return buildHeaders(headers, currentTokenResult);
    }));
    const retryDelay = ConfigHelper_1.default.toInteger(process.env.NEXT_PUBLIC_AUTH_RETRY_DELAY, 100);
    const retryLink = new retry_1.RetryLink({
        delay: { initial: retryDelay, max: Number.POSITIVE_INFINITY },
        attempts: handleRetry
    });
    return {
        // eslint-disable-next-line unicorn/prefer-spread
        authLink: authHeaderLink.concat(retryLink),
        retryFunction: handleRetry
    };
}
exports.default = authLink;
//# sourceMappingURL=authLink.js.map